
import Vue from 'vue';
import $ from 'jquery';
import Component from 'vue-class-component';
import { Action, Mutation, State, Getter } from "vuex-class";
import FlagIcon from '@/components/icons/FlagIcon.vue';
import { clearToken, getToken } from '@/functions/token.function';
import { AxiosError, AxiosResponse } from 'axios';
import ModalCodeInvalid from '@/views/code/ModalCodeInvalid.vue';
import { Watch } from 'vue-property-decorator';

@Component({
	components: {
		ModalCodeInvalid,
		FlagIcon,
	},
})
export default class AppNavbar extends Vue {
	@Action('getCodeDetail', { namespace: 'general' })
	private getCodeDetail: any;
	@Action('getTutorial', { namespace: 'general' })
	private getTutorial: any;
	@Action('completeTutorial', { namespace: 'general' })
	private completeTutorial: any;

	@State('user', { namespace: 'auth' })
	private user!: any;
	@State('locale')
	private locale!: string;

	@Getter('shouldHighlightCourses', { namespace: 'general' })
	private shouldHighlightCourses!: boolean;
	@Getter('shouldHighlightMyCourses', { namespace: 'general' })
	private shouldHighlightMyCourses!: boolean;
	@Getter('shouldHighlightRedeemCode', { namespace: 'general' })
	private shouldHighlightRedeemCode!: boolean;
	@Getter('activeCoachmark', { namespace: 'general' })
	private activeCoachmark!: number;

	@Mutation('mutationLocale')
	private mutationLocale: any;
	@Mutation("SET_ACTIVE_COACHMARK", { namespace: "general" })
	private setActiveCoachmark!: (coachmark: number) => void;
	@Mutation("RESET_ACTIVE_COACHMARK", { namespace: "general" })
	private resetActiveCoachmark!: () => void;

	private search = this.$route.query?.search || '';
	private code = '';
	private showTour = false;
	private isDesktop = false;
	private tourCardStyle: { top: string; left: string } = {
		top: '80px',
		left: '12px',
	};

	private get availableToken() {
		return getToken();
	}

	private get shouldShowTour() {
		return this.showTour && this.isDesktop && this.user;
	}

	private handleLogout() {
		clearToken();
		this.$router.push({
			name: 'login',
		});
	}

	private goToHome() {
		this.$router.push({
			name: 'home',
		});
	}

	private goToProfile() {
		this.$router.push({
			name: 'profile',
		});
	}

	private goToCourses() {
		this.$router.push({
			name: 'courses',
		});
	}

	private goToMyCourses() {
		this.$router.push({
			name: 'my-courses',
		});
	}

	private goToSearchCourses() {
		this.$router.push({
			name: 'courses',
			query: {
				search: this.search,
			},
		});
	}

	private goToCode() {
		if ([null, "", undefined].includes(this.code)) {
			this.$bvModal.show('modal-code-invalid');
			return;
		}

		this.getCodeDetail({
			params: {
				code: this.code,
			},
			success: (response: AxiosResponse) => {
				let data = response.data.data;
				let isCourse = data?.course_id;
				let price = isCourse ? data?.course?.price : data?.bundle_course?.price;
				let offerPrice = isCourse
					? data?.course?.offer_price
					: data?.bundle_course?.offer_price;
				let codePrice = data?.price;
				this.$router.push({
					name: 'checkout',
					query: {
						t: Date.now().toString(),
					},
					params: {
						course_id: data?.course_id,
						bundle_course_id: data?.bundle_course_id,
						price: price,
						offer_price: offerPrice,
						code_price: codePrice,
						name: data?.course?.name || data?.bundle_course?.name,
						code: data,
					},
				});
			},
			fail: (error: AxiosError) => {
				this.$bvModal.show('modal-code-invalid');
			},
		});
	}

	private navbarToggle() {
		$('#main-nav').toggleClass('navbar-expanded');
		$('#nav-mobile').toggleClass('show');
	}

	private checkDesktop() {
		this.isDesktop = window.innerWidth >= 1024; // Bootstrap lg breakpoint
	}

	private initializeDesktopTour() {
		if (this.isDesktop && typeof this.activeCoachmark === 'number' && [1, 2, 3, 4].includes(this.activeCoachmark)) {
			this.showTour = true;
			this.$nextTick(this.positionTourCard);
		}
	}

	private positionTourCard() {
		const card = this.$el.querySelector('.tour-card') as HTMLElement | null;
		const targetSelector = this.activeCoachmark === 1
			? '.tour-home-link'
			: '.tour-highlight';
		const target = this.$el.querySelector(targetSelector) as HTMLElement | null;
		if (!card) return;

		const targetRect = target?.getBoundingClientRect();
		const viewportPadding = 12;
		const cardWidth = Math.min(card.offsetWidth || 400, window.innerWidth - viewportPadding * 2);
		// Keep the welcome card below Home; align the other cards to the
		// highlighted navigation item while keeping every card in the viewport.
		const preferredLeft = targetRect
			? targetRect.left
			: viewportPadding;
		const left = Math.min(
			Math.max(viewportPadding, preferredLeft),
			window.innerWidth - cardWidth - viewportPadding,
		);
		const top = targetRect
			? Math.max(80, targetRect.bottom + 16)
			: 80;

		this.tourCardStyle = {
			top: `${top}px`,
			left: `${left}px`,
		};
	}

	private getUserTutorial() {
		const tutorialPromises = [1, 2, 3, 4].map(coachmarkNumber => {
			return new Promise((resolve, reject) => {
				this.getTutorial({
					params: {
						feature: `home:coachmark:${coachmarkNumber}`
					},
					success: (res: AxiosResponse) => {
						resolve(res.data.data);
					},
					error: (err: any) => {
						console.error(`Error fetching tutorial ${coachmarkNumber}:`, err);
						reject(err);
					}
				});
			});
		});

		Promise.all(tutorialPromises).then((responses: any[]) => {
			// Find the lowest incomplete coachmark (ascending order)
			for (let i = 0; i < responses.length; i++) {
				const tutorial = responses[i];

				if (tutorial && !tutorial.is_completed) {
					this.activeCoachmark = i + 1; // Set local state
					this.setActiveCoachmark(i + 1); // Update Vuex store
					this.initializeDesktopTour();
					return; // Exit early once we find the first incomplete one
				}
			}
		}).catch((error) => {
			console.error("Error fetching tutorials:", error);
		});
	}

	private nextButtonClicked() {
		this.completeTutorial({
			params: {
				feature: `home:coachmark:${this.activeCoachmark}`
			},
		})
		if (this.activeCoachmark < 4) {
			this.setActiveCoachmark(this.activeCoachmark + 1);
			this.$nextTick(this.positionTourCard);
		} else {
			this.closeTour();
		}
	}

	private previousCoachmark(coachmark: number) {
		this.setActiveCoachmark(coachmark);
		this.$nextTick(this.positionTourCard);
	}

	private closeTour() {
		this.showTour = false;
		this.resetActiveCoachmark();
	}

	private mounted() {
		this.checkDesktop();
		this.getUserTutorial();
		window.addEventListener("resize", this.checkDesktop);
		window.addEventListener("resize", this.positionTourCard);
	}

	private beforeDestroy() {
		window.removeEventListener("resize", this.checkDesktop);
		window.removeEventListener("resize", this.positionTourCard);
	}
}
